import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import TemplateEditorModal from './TemplateEditorModal';
import { useLanguage } from '../../../contexts/LanguageContext';

const TemplateManagementSection = ({ currentLanguage }) => {
  const { t } = useLanguage();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('edit');

  const handleNewTemplate = () => {
    alert(t('documents.newTemplate'));
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handlePreviewTemplate = (template) => {
    setSelectedTemplate(template);
    setModalMode('preview');
    setIsModalOpen(true);
  };

  const templates = [
    {
      id: 'tpl-001',
      name: { fr: 'Contrat Omra Standard', ar: 'عقد العمرة القياسي' },
      type: 'contract',
      language: 'both',
      lastModified: new Date('2026-01-20'),
      usageCount: 156,
      icon: 'FileText',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      id: 'tpl-002',
      name: { fr: 'Attestation Hajj', ar: 'شهادة الحج' },
      type: 'attestation',
      language: 'both',
      lastModified: new Date('2026-01-18'),
      usageCount: 89,
      icon: 'Award',
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      id: 'tpl-003',
      name: { fr: 'Liste Officielle', ar: 'القائمة الرسمية' },
      type: 'official_list',
      language: 'both',
      lastModified: new Date('2026-01-25'),
      usageCount: 45,
      icon: 'List',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10'
    },
    {
      id: 'tpl-004',
      name: { fr: 'Convocation Départ', ar: 'استدعاء المغادرة' },
      type: 'convocation',
      language: 'both',
      lastModified: new Date('2026-01-22'),
      usageCount: 234,
      icon: 'Bell',
      color: 'text-warning',
      bgColor: 'bg-warning/10'
    }
  ];

  const formatDate = (date) => {
    // Always use en-US locale for universal date format (Western numerals)
    return new Intl.DateTimeFormat('en-US', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    })?.format(date);
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-warm">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">
            {t('documents.templateManagement')}
          </h2>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            size="sm"
            onClick={handleNewTemplate}
          >
            {t('documents.newTemplate')}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 md:p-6">
        {templates?.map(template => (
          <div
            key={template?.id}
            className={`
              border rounded-lg p-4 cursor-pointer transition-smooth
              ${selectedTemplate?.id === template?.id
                ? 'border-primary bg-primary/5 shadow-warm'
                : 'border-border hover:border-primary/50 hover:shadow-warm-sm'
              }
            `}
            onClick={() => setSelectedTemplate(template)}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className={`w-12 h-12 rounded-lg ${template?.bgColor} flex items-center justify-center flex-shrink-0`}>
                <Icon name={template?.icon} size={24} className={template?.color} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-foreground mb-1 truncate">
                  {template?.name?.[currentLanguage]}
                </h3>
                <div className="flex items-center gap-2">
                  <span className="caption text-muted-foreground">
                    {t('documents.bilingual')}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-muted-foreground" />
                  <span className="caption text-muted-foreground">
                    {template?.usageCount} {t('documents.usages')}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm text-muted-foreground mb-3">
              <span className="flex items-center gap-1">
                <Icon name="Calendar" size={14} />
                {formatDate(template?.lastModified)}
              </span>
            </div>

            <div className="flex gap-2">
              <button 
                onClick={() => handleEditTemplate(template)}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary/10 text-primary rounded-md hover:bg-primary/20 transition-smooth text-sm">
                <Icon name="Edit" size={14} />
                {t('documents.modify')}
              </button>
              <button 
                onClick={() => handlePreviewTemplate(template)}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-muted text-foreground rounded-md hover:bg-muted/80 transition-smooth text-sm">
                <Icon name="Eye" size={14} />
                {t('documents.preview')}
              </button>
            </div>
          </div>
        ))}
      </div>
      {selectedTemplate && (
        <div className="p-4 md:p-6 border-t border-border bg-muted/30">
          <h3 className="font-semibold text-foreground mb-4">
            {t('documents.templateCustomization')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                {t('documents.header')}
              </label>
              <div className="flex items-center gap-2 p-3 bg-card border border-border rounded-md">
                <Icon name="Image" size={18} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {t('documents.agencyLogo')}
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                {t('documents.footer')}
              </label>
              <div className="flex items-center gap-2 p-3 bg-card border border-border rounded-md">
                <Icon name="FileText" size={18} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {t('documents.legalInfo')}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <TemplateEditorModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        template={selectedTemplate}
        currentLanguage={currentLanguage}
        mode={modalMode}
      />
    </div>
  );
};

export default TemplateManagementSection;